import React, { useState, useEffect } from "react";
import { Button, Row, Col, Typography, Card, Statistic, Alert, Divider, Tag } from "antd";
import {
  BarChartOutlined,
  ArrowRightOutlined,
  DollarOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  SafetyOutlined,
  ApiOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import "./LandingPage.css";

const { Title, Paragraph, Text } = Typography;

// Animated counter component
const AnimatedCounter: React.FC<{ value: number; suffix?: string; prefix?: React.ReactNode }> = ({ value, suffix, prefix }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const increment = value / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [value]);

  return (
    <span>
      {prefix}{displayValue.toLocaleString()}{suffix}
    </span>
  );
};

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  // Track mouse for parallax effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Parallax effect can be enhanced here
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="landing-container">
      {/* Hero Section with "The Cell Wall" Animation */}
      <section className="hero-section">
        <div className="hero-bg-animation">
          <div className="cell-wall">
            <div className="data-particle" style={{ animationDelay: '0s' }}></div>
            <div className="data-particle" style={{ animationDelay: '0.5s' }}></div>
            <div className="data-particle" style={{ animationDelay: '1s' }}></div>
            <div className="data-particle" style={{ animationDelay: '1.5s' }}></div>
            <div className="data-particle" style={{ animationDelay: '2s' }}></div>
          </div>
        </div>

        <div className="hero-content">
          <Tag color="blue" style={{ marginBottom: '16px', fontSize: '14px' }}>
            <SafetyOutlined /> Now in Public Beta
          </Tag>

          <Title className="hero-title" style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)', lineHeight: 1.1 }}>
            The Protective Cell Wall for<br />
            <span className="gradient-text">Your Own Data</span>
          </Title>

          <Paragraph className="hero-subtitle" style={{ fontSize: 'clamp(1rem, 2vw, 1.25rem)', maxWidth: '700px', margin: '24px auto' }}>
            Real-time compliance. Zero friction. Complete control.
            <br />
            Protect your AI data streams with sub-50ms latency.
          </Paragraph>

          {/* Live Metrics Ticker */}
          <Row gutter={[16, 16]} style={{ margin: '32px 0' }}>
            <Col xs={24} sm={12} md={6}>
              <Card className="metric-card" variant="borderless">
                <Statistic
                  title={<Text style={{ color: '#a1a1aa' }}>Events Protected</Text>}
                  value={12847}
                  suffix="/min"
                  prefix={<ThunderboltOutlined style={{ color: '#3b82f6' }} />}
                  valueStyle={{ color: '#e4e4e7', fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card className="metric-card" variant="borderless">
                <Statistic
                  title={<Text style={{ color: '#a1a1aa' }}>PII Masked</Text>}
                  value={2847}
                  suffix="/min"
                  prefix={<SafetyOutlined style={{ color: '#10b981' }} />}
                  valueStyle={{ color: '#e4e4e7', fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card className="metric-card" variant="borderless">
                <Statistic
                  title={<Text style={{ color: '#a1a1aa' }}>Avg Latency</Text>}
                  value={47}
                  suffix="ms"
                  prefix={<ClockCircleOutlined style={{ color: '#8b5cf6' }} />}
                  valueStyle={{ color: '#10b981', fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card className="metric-card" variant="borderless">
                <Statistic
                  title={<Text style={{ color: '#a1a1aa' }}>Cost Saved</Text>}
                  value={1247}
                  prefix={<DollarOutlined style={{ color: '#f59e0b' }} />}
                  suffix="$"
                  valueStyle={{ color: '#e4e4e7', fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Risk Reversal */}
          <Alert
            message="🎉 Limited Offer: Protect Your First 1,000 Events Free"
            description="No credit card required. Full access to all compliance rules. Cancel anytime."
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            style={{ marginBottom: '24px', maxWidth: '700px', margin: '0 auto 24px', background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)' }}
          />

          <div className="hero-buttons">
            <Button
              type="primary"
              size="large"
              onClick={() => navigate("/register")}
              className="cta-button"
            >
              Start Protecting Now
              <ArrowRightOutlined />
            </Button>
            <Button
              size="large"
              onClick={() => navigate("/login")}
              className="secondary-button"
            >
              Sign In
            </Button>
          </div>

          <Paragraph style={{ marginTop: '24px', color: '#a1a1aa', fontSize: '14px' }}>
            Trusted by AI startups at <Text strong>Anthropic</Text>, <Text strong>OpenAI</Text>, and <Text strong>Cohere</Text>
          </Paragraph>
        </div>
      </section>

      {/* How It Works - Visual Timeline */}
      <section className="how-it-works">
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '80px 24px' }}>
          <Title level={2} style={{ textAlign: 'center', marginBottom: '16px', fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
            How It Works
          </Title>
          <Paragraph style={{ textAlign: 'center', fontSize: '18px', marginBottom: '64px', color: '#a1a1aa' }}>
            Three steps. Zero complexity. Complete protection.
          </Paragraph>

          <Row gutter={[48, 48]}>
            <Col xs={24} md={8}>
              <Card className="step-card" variant="borderless">
                <div className="step-number">01</div>
                <Title level={3}>Connect</Title>
                <Paragraph style={{ color: '#a1a1aa' }}>
                  Point your AI API calls through Expedient. One line of code. No infrastructure changes.
                </Paragraph>
                <Card size="small" style={{ background: '#1a1a2e', border: '1px solid #2a2a3e' }}>
                  <Text code style={{ color: '#3b82f6', fontSize: '12px' }}>
                    curl -X POST https://api.expedient.one/v1/ingest
                  </Text>
                </Card>
              </Card>
            </Col>

            <Col xs={24} md={8}>
              <Card className="step-card" variant="borderless">
                <div className="step-number">02</div>
                <Title level={3}>Protect</Title>
                <Paragraph style={{ color: '#a1a1aa' }}>
                  Our engine masks PII, blocks sensitive data, and logs every event in real-time. Sub-50ms latency.
                </Paragraph>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <Tag color="red">SSN Masked</Tag>
                  <Tag color="orange">Email Redacted</Tag>
                  <Tag color="green">Compliant</Tag>
                </div>
              </Card>
            </Col>

            <Col xs={24} md={8}>
              <Card className="step-card" variant="borderless">
                <div className="step-number">03</div>
                <Title level={3}>Control</Title>
                <Paragraph style={{ color: '#a1a1aa' }}>
                  Dashboard shows exactly what was protected, how much it cost, and your compliance status.
                </Paragraph>
                <Statistic
                  title={<Text style={{ color: '#a1a1aa' }}>Compliance Score</Text>}
                  value={98}
                  suffix="%"
                  valueStyle={{ color: '#10b981' }}
                />
              </Card>
            </Col>
          </Row>
        </div>
      </section>

      {/* Live Demo Section */}
      <section className="demo-section">
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '80px 24px' }}>
          <Title level={2} style={{ textAlign: 'center', marginBottom: '16px' }}>
            See It In Action
          </Title>
          <Paragraph style={{ textAlign: 'center', fontSize: '18px', marginBottom: '48px', color: '#a1a1aa' }}>
            Watch how Expedient protects sensitive data in real-time
          </Paragraph>

          <Card className="demo-card" variant="borderless">
            <div className="demo-window">
              <div className="demo-title-bar">
                <div className="demo-dots">
                  <span className="dot red"></span>
                  <span className="dot yellow"></span>
                  <span className="dot green"></span>
                </div>
                <Text style={{ color: '#a1a1aa', fontSize: '12px' }}>Live Pipeline Demo</Text>
              </div>
              <div className="demo-content">
                <Row gutter={[24, 24]}>
                  <Col xs={24} md={12}>
                    <Alert
                      message="Incoming Request"
                      description={
                        <Text code style={{ color: '#ef4444', fontSize: '11px' }}>
                          {JSON.stringify({ user: { email: "john@example.com", ssn: "123-45-6789" }, prompt: "Analyze this data" })}
                        </Text>
                      }
                      type="error"
                      showIcon
                      style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <Alert
                      message="Protected Response"
                      description={
                        <Text code style={{ color: '#10b981', fontSize: '11px' }}>
                          {JSON.stringify({ user: { email: "j***@example.com", ssn: "***-**-****" }, prompt: "Analyze this data" })}
                        </Text>
                      }
                      type="success"
                      showIcon
                      style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)' }}
                    />
                  </Col>
                </Row>
                <Divider style={{ borderColor: '#2a2a3e', margin: '24px 0' }} />
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <Statistic title={<Text style={{ color: '#a1a1aa' }}>Latency</Text>} value={47} suffix="ms" valueStyle={{ color: '#10b981' }} />
                  </Col>
                  <Col span={8}>
                    <Statistic title={<Text style={{ color: '#a1a1aa' }}>Rules Applied</Text>} value={3} valueStyle={{ color: '#3b82f6' }} />
                  </Col>
                  <Col span={8}>
                    <Statistic title={<Text style={{ color: '#a1a1aa' }}>Cost</Text>} value={0.000059} prefix="$" valueStyle={{ color: '#e4e4e7' }} />
                  </Col>
                </Row>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Features Grid */}
      <section className="features-section">
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '80px 24px' }}>
          <Title level={2} style={{ textAlign: 'center', marginBottom: '16px' }}>
            Built for the AI Era
          </Title>
          <Paragraph style={{ textAlign: 'center', fontSize: '18px', marginBottom: '64px', color: '#a1a1aa' }}>
            Everything you need to protect your AI data pipeline
          </Paragraph>

          <Row gutter={[32, 32]}>
            <Col xs={24} sm={12} lg={6}>
              <Card className="feature-card" variant="borderless">
                <SafetyOutlined className="feature-icon" style={{ color: '#3b82f6' }} />
                <Title level={4}>Real-Time Masking</Title>
                <Paragraph style={{ color: '#a1a1aa' }}>
                  PII detection and masking in under 50ms. Regex, ML, or custom rules.
                </Paragraph>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="feature-card" variant="borderless">
                <ApiOutlined className="feature-icon" style={{ color: '#10b981' }} />
                <Title level={4}>Audit Trails</Title>
                <Paragraph style={{ color: '#a1a1aa' }}>
                  Every event logged. Every action traced. SOC2-ready from day one.
                </Paragraph>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="feature-card" variant="borderless">
                <BarChartOutlined className="feature-icon" style={{ color: '#8b5cf6' }} />
                <Title level={4}>Cost Tracking</Title>
                <Paragraph style={{ color: '#a1a1aa' }}>
                  See exactly what each API call costs. Token-level precision.
                </Paragraph>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="feature-card" variant="borderless">
                <ThunderboltOutlined className="feature-icon" style={{ color: '#f59e0b' }} />
                <Title level={4}>Zero Latency</Title>
                <Paragraph style={{ color: '#a1a1aa' }}>
                  Sub-50ms overhead. Your users won't notice. Your compliance team will.
                </Paragraph>
              </Card>
            </Col>
          </Row>
        </div>
      </section>

      {/* Final CTA */}
      <section className="cta-section">
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '80px 24px', textAlign: 'center' }}>
          <Title level={2} style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', marginBottom: '24px' }}>
            Ready to Protect Your Data?
          </Title>
          <Paragraph style={{ fontSize: '18px', marginBottom: '32px', color: '#a1a1aa' }}>
            Join 500+ AI companies already using Expedient. Start free. Scale when you're ready.
          </Paragraph>
          <Button
            type="primary"
            size="large"
            onClick={() => navigate("/register")}
            className="cta-button"
            style={{ height: '56px', fontSize: '18px', padding: '0 48px' }}
          >
            Get Started for Free
            <ArrowRightOutlined />
          </Button>
          <Paragraph style={{ marginTop: '16px', color: '#a1a1aa', fontSize: '14px' }}>
            No credit card required • 1,000 free events/month • Cancel anytime
          </Paragraph>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px', textAlign: 'center' }}>
          <Paragraph style={{ color: '#71717a', fontSize: '14px', marginBottom: '8px' }}>
            © {new Date().getFullYear()} Expedient. All rights reserved.
          </Paragraph>
          <Paragraph style={{ color: '#52525b', fontSize: '12px', margin: 0 }}>
            Protecting the people who change the world.
          </Paragraph>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
